import { Check, X } from '@phosphor-icons/react';
import type { FriendRequest, FriendSuggestion, PublicUser } from '@sirocco/shared';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { users } from '../../api/endpoints';
import { ScreenError, ScreenLoader, SubScreen } from '../../components/layout/Screens';
import { Avatar } from '../../components/ui/Avatar';
import { BadgeEmoji, EmptyState } from '../../components/ui/Bits';
import { Button } from '../../components/ui/Button';
import { SearchField } from '../../components/ui/Field';
import { useAsync, useDebounced } from '../../hooks/useAsync';
import { useSession } from '../../store/SessionContext';
import { profilePath } from '../../utils/routes';
import { t } from '../../i18n';
import { sansAccents } from '../annuaire/commun';
import { useMonRole } from '../moderation/Gestes';

/**
 * La fenetre de suggestions, et le renouvellement du jour.
 *
 * On ne renouvelle les suggestions qu'une fois par jour : tirer la liste
 * a l'infini faisait defiler tout le spot comme un catalogue. Le second
 * geste de la journee rend donc les memes visages. L'etat vit sur
 * l'appareil, avec la fenetre en cours : l'ecran se demonte des qu'on
 * ouvre un profil, et l'application peut se relancer dans la journee
 * sans rendre le droit de renouveler.
 */
const CLE_SUGGESTIONS = 'sirocco.suggestions';

interface Renouvellement {
  uid: string;
  /** Le jour du dernier renouvellement, a l'heure du telephone. */
  jour: string;
  tour: number;
}

function lireRenouvellement(uid: string): Renouvellement {
  try {
    const lu = JSON.parse(localStorage.getItem(CLE_SUGGESTIONS) ?? 'null') as Renouvellement | null;
    if (lu?.uid === uid && Number.isInteger(lu.tour)) return lu;
  } catch {
    // Stockage illisible ou ferme : on repart de la premiere fenetre.
  }
  return { uid, jour: '', tour: 0 };
}

function ecrireRenouvellement(renouvellement: Renouvellement): void {
  try {
    localStorage.setItem(CLE_SUGGESTIONS, JSON.stringify(renouvellement));
  } catch {
    // Sans stockage, la limite ne tient que le temps de la session.
  }
}

function aujourdhui(): string {
  const maintenant = new Date();
  return `${maintenant.getFullYear()}-${maintenant.getMonth() + 1}-${maintenant.getDate()}`;
}

/**
 * Ajouter des amis : les demandes recues d'abord, les suggestions du
 * spot ensuite. Une recherche remplace les suggestions par ses resultats.
 *
 * L'administrateur voit tous les comptes de l'application a la place des
 * suggestions, par ordre alphabetique : c'est d'ici qu'il ouvre un profil
 * pour agir dessus, sans avoir a connaitre un pseudo.
 */
export function AddFriendsScreen() {
  const { server, user } = useSession();
  const uid = user?.id ?? '';
  /*
    Ouvrir l'ecran vaut lecture : la pastille s'eteint des l'arrivee, pas
    a la reponse. Laisser une demande sans suite est un choix, et le point
    n'a pas a le rappeler indefiniment.
  */
  useEffect(() => {
    void users.markRequestsSeen().catch(() => undefined);
  }, []);
  const [search, setSearch] = useState('');
  // On n'interroge pas l'API a chaque frappe.
  const debounced = useDebounced(search);
  /*
    La liste sans recherche est retenue : c'est celle qu'on retrouve en
    revenant d'un profil. Sans ce cache, l'ecran repartait de zero a
    chaque retour — d'ou le petit chargement qui cassait le geste. Les
    resultats d'une recherche, eux, ne se gardent pas : ressortir de
    vieux resultats sur une frappe identique serait pire que de relire.
  */
  /*
    De quelle fenetre de suggestions on est.

    Un cran de plus a chaque fois qu'on tire la liste vers le bas : elle
    tourne dans le vivier du spot plutot que de montrer eternellement les
    dix memes — voir `friends.listSuggestions`.
  */
  const [tour, setTour] = useState(() => lireRenouvellement(uid).tour);

  const admin = useMonRole() === 'admin';
  /* Lue seulement pour l'administrateur : les autres n'en ont pas l'usage. */
  const tous = useAsync(
    () => (admin ? users.tousLesMembres() : Promise.resolve(null)),
    [admin],
    admin ? 'tous-les-membres' : undefined,
  );

  const { data, loading, error, reload } = useAsync(
    () => users.friends(debounced, tour),
    [debounced, tour],
    /*
      Une seule entree pour toutes les fenetres.

      Avec une cle par fenetre, tirer la liste changeait de cle : la
      donnee affichee tombait a rien, le tourniquet prenait toute la
      place, et les visages revenaient apres. On garde donc la meme cle
      — c'est la meme question, seule la reponse tourne — et la liste
      precedente reste sous les yeux le temps que la suivante arrive.
    */
    debounced.trim() ? undefined : 'ajouter-des-amis',
  );

  /*
    La roue tourne jusqu'a ce que la nouvelle fenetre soit la.

    Avancer d'un cran se fait tout de suite, mais la liste arrive apres :
    rendre la main aussitot aurait arrete la roue avant que les nouveaux
    visages paraissent, et le geste aurait semble ne rien donner. On
    attend donc la reponse — une nouvelle donnee, ou une erreur. Huit
    secondes au plus : une roue ne tourne pas indefiniment pour un
    reseau qui ne repond pas.
  */
  const quandArrive = useRef<(() => void) | null>(null);
  useEffect(() => {
    quandArrive.current?.();
    quandArrive.current = null;
  }, [data, error]);

  function proposerDAutres(): Promise<void> {
    const jour = aujourdhui();
    const deja = lireRenouvellement(uid);
    /* Deja renouvele aujourd'hui : la liste se relit, avec les memes visages. */
    const renouveler = deja.jour !== jour;
    return new Promise((fin) => {
      const garde = window.setTimeout(fin, 8000);
      quandArrive.current = () => {
        window.clearTimeout(garde);
        fin();
      };
      if (!renouveler) {
        reload();
        return;
      }
      const suivant = tour + 1;
      ecrireRenouvellement({ uid, jour, tour: suivant });
      setTour(suivant);
    });
  }

  const searching = debounced.trim().length > 0;
  const listed = searching ? (data?.results ?? []) : (data?.suggestions ?? []);

  return (
    <SubScreen
      title={t.messages.ajouterAmis.titre}
      /*
        Tirer la liste vers le bas propose d'autres membres.

        Le meme geste que sur le fil et les evenements, et la meme
        attente : on tire pour avoir du neuf. Ici le neuf ne vient pas du
        reseau mais du vivier du spot — la fenetre avance d'un cran et
        montre ceux qu'on n'avait pas encore vus.
      */
      onRefresh={
        /* Tirer la liste de tous les comptes la relit : il n'y a rien a faire tourner. */
        admin
          ? () => {
              reload();
              return tous.refresh();
            }
          : proposerDAutres
      }
      /* La roue du fil : on tire pour avoir du neuf, il faut le voir venir. */
      roue
    >
      <SearchField
        tone="spot"
        className="ms-search ms-search--fine search--croix-du-fil"
        placeholder={t.messages.commun.rechercherPseudo}
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        onClear={() => setSearch('')}
      />

      {/* Le tourniquet ne parait que s'il n'y a rien a montrer : la liste
          en cache se reaffiche tout de suite, et la relecture se fait en
          silence derriere. */}
      {loading && !data && <ScreenLoader />}
      {error && <ScreenError message={error} onRetry={reload} />}

      {data && (
        <>
          {data.requests.length > 0 && (
            <section>
              <h2 className="ms-section">{t.messages.ajouterAmis.demandesRecues(data.requests.length)}</h2>
              {data.requests.map((request) => (
                <RequestCard key={request.id} request={request} onAnswered={reload} />
              ))}
            </section>
          )}

          {admin && !searching ? (
            <TousLesMembres membres={tous.data} onRequested={() => void tous.refresh()} />
          ) : (
          <section>
            <h2 className="ms-section ms-section--spaced">
              {t.messages.ajouterAmis.suggestions(server?.name ?? null)}
            </h2>

            {searching && listed.length === 0 ? (
              <EmptyState title={t.messages.commun.aucunMembre} />
            ) : (
              listed.map((suggestion) => (
                <SuggestionCard
                  key={suggestion.user.id}
                  suggestion={suggestion}
                  onRequested={reload}
                />
              ))
            )}
          </section>
          )}
        </>
      )}
    </SubScreen>
  );
}

/**
 * Tous les comptes, sous la lettre de leur pseudo.
 *
 * Comme l'annuaire d'une ecole : la liste arrive triee, on ne fait que la
 * couper. Un pseudo qui ne commence pas par une lettre latine se range
 * sous « # », en tete.
 */
function TousLesMembres({
  membres,
  onRequested,
}: {
  membres: FriendSuggestion[] | null;
  onRequested: () => void;
}) {
  if (!membres) return <ScreenLoader />;

  const groupes = new Map<string, FriendSuggestion[]>();
  for (const membre of membres) {
    const premiere = sansAccents(membre.user.username.charAt(0)).toUpperCase();
    const lettre = /^[A-Z]$/.test(premiere) ? premiere : '#';
    groupes.set(lettre, [...(groupes.get(lettre) ?? []), membre]);
  }
  const lettres = [...groupes].sort(([a], [b]) =>
    a === '#' ? -1 : b === '#' ? 1 : a.localeCompare(b),
  );

  return (
    <section>
      <h2 className="ms-section ms-section--spaced">
        {t.messages.ajouterAmis.tousLesMembres(membres.length)}
      </h2>
      {lettres.map(([lettre, liste]) => (
        <div key={lettre}>
          <h3 className="an__initiale">{lettre}</h3>
          {liste.map((membre) => (
            <SuggestionCard key={membre.user.id} suggestion={membre} onRequested={onRequested} />
          ))}
        </div>
      ))}
    </section>
  );
}

/** Demande recue : on repond sans quitter l'ecran. */
function RequestCard({ request, onAnswered }: { request: FriendRequest; onAnswered: () => void }) {
  const [pending, setPending] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  /**
   * Repondre a une demande.
   * L'echec doit se voir : sans ce message, une acceptation refusee par la
   * base laissait la carte en place sans rien dire, et on croyait que le
   * bouton ne marchait pas.
   */
  async function answer(accept: boolean) {
    setPending(true);
    setFailure(null);
    try {
      if (accept) await users.acceptRequest(request.id);
      else await users.declineRequest(request.id);
      onAnswered();
    } catch (cause) {
      setFailure(cause instanceof Error ? cause.message : t.sorties.commun.actionImpossible);
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="card ms-person">
      <PersonLink user={request.from} failure={failure} />

      <button
        className="ms-person__act ms-person__act--yes"
        aria-label={t.messages.ajouterAmis.accepter(request.from.username)}
        disabled={pending}
        onClick={() => void answer(true)}
      >
        <Check size={20} weight="bold" />
      </button>

      <button
        className="ms-person__act ms-person__act--no"
        aria-label={t.messages.ajouterAmis.refuser(request.from.username)}
        disabled={pending}
        onClick={() => void answer(false)}
      >
        <X size={20} weight="bold" />
      </button>
    </div>
  );
}

/** Suggestion du spot, ou resultat de recherche. */
function SuggestionCard({
  suggestion,
  onRequested,
}: {
  suggestion: FriendSuggestion;
  onRequested: () => void;
}) {
  const [pending, setPending] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const requested = suggestion.status === 'requested';
  const incoming = suggestion.status === 'incoming';

  async function request() {
    setPending(true);
    setFailure(null);
    try {
      // Une demande deja recue s'accepte : c'est ce que dit le bouton.
      if (incoming) await users.acceptRequest(suggestion.user.id);
      else await users.requestFriend(suggestion.user.id);
      onRequested();
    } catch (cause) {
      setFailure(cause instanceof Error ? cause.message : t.sorties.commun.actionImpossible);
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="card ms-person">
      <PersonLink user={suggestion.user} failure={failure} />

      {/* Une demande deja partie passe en pastel : elle n'appelle plus le doigt.
          Rien du tout en face du compte de l'application : il ne se lie a
          personne, la carte ne mene qu'a son profil. */}
      {!suggestion.user.officiel && (
        <Button
          variant={requested || suggestion.status === 'friends' ? 'soft' : 'primary'}
          size="sm"
          loading={pending}
          disabled={suggestion.status === 'friends'}
          onClick={() => void request()}
        >
          {LABELS[suggestion.status]}
        </Button>
      )}
    </div>
  );
}

/**
 * Le visage, le pseudo, et la porte vers le profil.
 *
 * La carte ne menait nulle part : on ne pouvait ni consulter un membre ni
 * lui ecrire depuis cet ecran, alors que c'est le seul endroit ou l'on
 * cherche quelqu'un qu'on ne connait pas encore. Elle s'ouvre desormais
 * d'un appui, sur toute la moitie gauche — le bouton d'action garde la
 * sienne.
 *
 * Plus de sous-titre non plus : « nouveau sur le spot », « deux amis en
 * commun » remplissaient une ligne sans jamais faire agir. Elle ne sert
 * plus qu'a dire ce qui vient d'echouer.
 */
function PersonLink({ user, failure }: { user: PublicUser; failure: string | null }) {
  const { user: moi } = useSession();

  return (
    <Link to={profilePath(user.id, moi?.id)} className="ms-person__open">
      <Avatar user={user} size={40} />
      <span className="ms-person__body">
        <span className="ms-person__name">
          {user.username}
          <BadgeEmoji user={user} />
        </span>
        {failure && <span className="ms-person__note">{failure}</span>}
      </span>
    </Link>
  );
}

/** Ce que le bouton de droite propose, selon l'etat de la relation. */
const LABELS: Record<FriendSuggestion['status'], string> = {
  none: t.messages.ajouterAmis.boutons.none,
  requested: t.messages.ajouterAmis.boutons.requested,
  incoming: t.messages.ajouterAmis.boutons.incoming,
  friends: t.messages.ajouterAmis.boutons.friends,
};
